import { useMemo, useState } from "react";
import { useCustomerInvoices } from "@/hooks/invoice/use-customer-invoices";
import { Loader2 } from "lucide-react";
import { columns } from "./columns";
import { DataTable } from "./data-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/classnames";
import { subDays, startOfYear } from "date-fns";
import {
  UNPAID_INVOICE_STATUS,
  PAST_DUE_INVOICE_STATUS,
  UNPAID_AND_OVERDUE_INVOICE_STATUS,
  PENDING_INVOICE_STATUS,
  PROCESSING_INVOICE_STATUS,
} from "@/constants/invoice";

const DATE_RANGE_OPTIONS = [
  { label: "All time", value: "all" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 60 days", value: "60" },
  { label: "Last 90 days", value: "90" },
  { label: "This year", value: "year" },
];

const QUICK_FILTERS = [
  { label: "All", value: "all" },
  { label: "Unpaid", value: "unpaid" },
  { label: "Past Due", value: "past_due" },
  { label: "Pending", value: "pending" },
  { label: "Scheduled", value: "scheduled" },
];

const STAT_TILES = [
  {
    key: "unpaid",
    label: "Unpaid",
    statuses: [UNPAID_INVOICE_STATUS],
    quickFilterValue: "unpaid",
  },
  {
    key: "past_due",
    label: "Past Due",
    statuses: [PAST_DUE_INVOICE_STATUS, UNPAID_AND_OVERDUE_INVOICE_STATUS],
    quickFilterValue: "past_due",
  },
  {
    key: "pending",
    label: "Pending",
    statuses: [PENDING_INVOICE_STATUS, PROCESSING_INVOICE_STATUS],
    quickFilterValue: "pending",
  },
  {
    key: "all",
    label: "All",
    statuses: null,
    quickFilterValue: "all",
  },
];

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val ?? 0);

function getDateCutoff(value) {
  const now = new Date();
  if (value === "30") return subDays(now, 30);
  if (value === "60") return subDays(now, 60);
  if (value === "90") return subDays(now, 90);
  if (value === "year") return startOfYear(now);
  return null;
}

function StatTile({ tile, invoices }) {
  const filtered =
    tile.statuses === null
      ? invoices
      : invoices.filter((inv) =>
          tile.statuses.includes(inv.invoice_info?.invoice_status?.toLowerCase() ?? "")
        );
  const total = filtered.reduce((sum, inv) => sum + (inv.invoice_info?.invoice_total ?? 0), 0);

  return (
    <div className="bg-white px-5 py-4 flex items-center justify-between lg:block lg:p-5 lg:rounded-xl lg:border lg:border-gray-200 lg:shadow-sm">
      <div className="flex items-baseline gap-2 lg:block">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
          {tile.label}
        </span>
        <span className="text-xs text-gray-400 lg:hidden">
          · {filtered.length} {filtered.length === 1 ? "invoice" : "invoices"}
        </span>
      </div>
      <div className="text-base font-semibold text-gray-900 tabular-nums lg:mt-2 lg:text-2xl lg:font-bold">
        {formatCurrency(total)}
      </div>
      <div className="hidden mt-1 text-sm text-gray-400 lg:block">
        {filtered.length} {filtered.length === 1 ? "invoice" : "invoices"}
      </div>
    </div>
  );
}

export default function CustomerInvoiceTable() {
  const { data: invoices = [], isLoading } = useCustomerInvoices();
  const [dateRange, setDateRange] = useState("all");
  const [quickFilter, setQuickFilter] = useState("all");

  const dateFiltered = useMemo(() => {
    const cutoff = getDateCutoff(dateRange);
    if (!cutoff) return invoices;
    return invoices.filter((inv) => {
      const raw = inv.invoice_info?.invoice_send_date;
      if (!raw) return true;
      const d = new Date(raw);
      return !isNaN(d.getTime()) && d >= cutoff;
    });
  }, [invoices, dateRange]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-40">
      {/* Stat tiles */}
      <div className="flex flex-col divide-y divide-gray-200 border border-gray-200 rounded-xl overflow-hidden lg:flex-none lg:divide-y-0 lg:border-0 lg:overflow-visible lg:rounded-none lg:grid lg:grid-cols-4 lg:gap-4">
        {STAT_TILES.map((tile) => (
          <StatTile key={tile.key} tile={tile} invoices={dateFiltered} />
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 mx-4 lg:mx-0 lg:flex-row lg:items-center">
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-full lg:w-[160px] h-9 text-sm bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_RANGE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="grid grid-cols-3 gap-1.5 lg:flex lg:items-center lg:gap-1.5">
          {QUICK_FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setQuickFilter(filter.value)}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm font-medium transition-colors text-center",
                quickFilter === filter.value
                  ? "bg-skin-primary text-skin-a11y"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <DataTable columns={columns} data={dateFiltered} quickFilter={quickFilter} />
    </div>
  );
}
